import React from 'react';
import {useLocation} from "react-router-dom";
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import Dropzone from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { parse } from 'csv-parse/lib/sync';
import { useSystemMessage } from '../../../../hooks/useSystemMessage';
import icon05 from '../../../../assets/images/ROOM_TaskFile_Re_05.png';
import {logError} from "../../../../libs/utils/logError";
import useAppRouter from "../../../../hooks/useAppRouter";
import useLoading from '../../../../hooks/useLoading';
import {roomCustomerApi} from '../../../../apis/admin/room/roomCustomerApi';

interface BulkCreateCustomerModalProps {
  onClosed: () => Promise<void>;
  onCreated: () => Promise<void>;
}

interface CustomerUserAttributes {
  userName: string,
  companyName: string,
  email: string,
  department: string,
  position: string,
}

export const BulkCreateCustomerModal = (props: BulkCreateCustomerModalProps) => {
  const MAX_IMPORT_COUNT = 50;

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const { onClosed, onCreated } = props;
  const { success, warn, danger } = useSystemMessage();
  const [importFile, setImportFile] = React.useState<File | undefined>();
  const location = useLocation();
  const { goto } = useAppRouter();
  const withLoading = useLoading();

  const createCustomerUser = async (customerAttributes: CustomerUserAttributes) => {
    try {
      await roomCustomerApi.create(customerAttributes, {});

    } catch (e) {
      await logError(
        location.pathname,
        '顧客ユーザーの作成に失敗しました。',
        e
      );

      throw e;
    }
  }

  const onSubmit = async () => {
    withLoading(async () => {

      if (importFile === undefined) {
        await danger('ファイルが選択されていません');
        return;
      }

      try {
        const reader = new FileReader();
        reader.onload = () => {
          withLoading(async () => {
            if (typeof reader.result !== 'string') {
              return;
            }

            const csvParseOptions = {
              columns: false,          // ヘッダーなし
              skip_empty_lines: true,  // 空行を無視する
              delimiter: ',',
            };
            const rows: { [key: string]: string }[] = parse(reader.result, csvParseOptions);
            const importUserEmails: string[] = [];
            let importSuccessCount = 0;

            try {
              const createCustomerPromises = rows.map(row => {
                const email = row[1];
                // 重複しているメールアドレスは無視する
                if (importUserEmails.includes(email)) {
                  return null;
                }

                importSuccessCount += 1;
                importUserEmails.push(email);

                return createCustomerUser({
                  userName: row[0],
                  companyName: "",
                  email: row[1].split('\r')[0],
                  department: "",
                  position: "",
                }).catch(() => {
                  importSuccessCount -= 1;
                });
              });

              if (importSuccessCount > MAX_IMPORT_COUNT) {
                await warn(`一度のインポートは${MAX_IMPORT_COUNT}件までです。`);
                return;
              }

              if (importSuccessCount === 0) {
                await warn('追加対象がありません。');
                return;
              }

              await Promise.all(createCustomerPromises);
            } catch (e) {
              console.error(e);
            }

            await success(`顧客を${importSuccessCount}件一括追加しました。`);
            await onClosed();
            await onCreated();
          });
        }
        reader.readAsText(importFile);

      } catch (e) {
        await logError(
          location.pathname,
          '顧客の一括追加に失敗しました。',
          e
        );

        goto('internalServerError');
      }

    });
  };

  const isCsvFile = (file: File): boolean => {
    return file.name.endsWith('.csv');
  }

  const handleDrop = async (acceptedFile: File) => {
    clearErrors('file');

    if (!isCsvFile(acceptedFile)) {
      setError('file', {
        type: 'invalidFileFormat',
        message: 'CSVファイルを選択してください',
      });
      return;
    }

    setImportFile(acceptedFile);
    setValue('file', acceptedFile);
  };

  const handleClearButtonClick = () => {
    setImportFile(undefined);
    setValue('file', undefined);
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={onClosed}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">
              顧客一括追加
            </h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClosed}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-modal-content">
              <section className="px-2">
                <input
                  type="hidden"
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register('file', { required: true })}
                />
                <Dropzone
                  multiple={false}
                  onDrop={(acceptedFiles) => handleDrop(acceptedFiles[0])}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...getRootProps({
                        className:
                          'h-48 bg-white flex items-center justify-center cursor-pointer',
                      })}
                    >
                      {importFile === undefined ? (
                        <>
                          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                          <input {...getInputProps()} />
                          <p className="text-sm text-primary font-semibold">
                            インポートファイル（CSV）をドラッグ＆ドロップしてください
                          </p>
                        </>
                      ) : (
                        <div className="flex flex-col justify-center w-36">
                          <div className="overflow-hidden relative w-full flex justify-center">
                            <div className="h-full w-full flex items-center justify-center">
                              <FontAwesomeIcon
                                icon={faFile}
                                size="2x"
                                className="text-gray-500"
                              />
                            </div>
                          </div>
                          <div className="flex justify-center w-full">
                            <p className="text-xs text-primary font-semibold truncate">
                              {importFile.name}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="text-xs my-2 outline-none focus:outline-none"
                            onClick={handleClearButtonClick}
                          >
                            ファイルをクリア
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </Dropzone>

                {errors.file && (errors.file.type === 'required') && (
                  <div>
                    <span className="text-red-500 text-xs">
                      インポートファイル（CSV）を指定してください
                    </span>
                  </div>
                )}
                {errors.file && (errors.file.type === 'invalidFileFormat') && (
                  <div>
                    <span className="text-red-500 text-xs">
                       { errors.file.message }
                    </span>
                  </div>
                )}
              </section>
            </div>
            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={onClosed}
                >
                  キャンセル
                </button>
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  一括追加
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};
